import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatLanguage } from '../i18n';
import { ChatService } from '../services/chatService';
import { Recording, RecorderState, useVoiceRecorder, VOICE_MIN_MS } from './useVoiceRecorder';

/** Where a spoken question is: not started, waiting for the microphone, being said, or being written down. */
export type VoicePhase = 'idle' | 'starting' | 'listening' | 'writing';

/** What the composer says when speaking did not end in a sent question. */
export type VoiceNotice = 'nothing' | 'blocked' | 'noMic' | 'failed' | 'tooMany' | 'unavailable';

/** Waiting for the microphone longer than this is the browser asking the guest: the composer says to allow it. */
export const VOICE_ASKING_MS = 400;

const refusalNotice: Partial<Record<RecorderState, VoiceNotice>> = { denied: 'blocked', unavailable: 'noMic', failed: 'failed' };

/** The languages the guest likely speaks, most likely first: the chat's language, then the device's. */
export const speakerLanguages = (chatLanguage: ChatLanguage) => {
   const device = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];
   return Array.from(new Set([chatLanguage, ...device].filter(Boolean)));
};

/**
 * A question asked aloud: `start` opens the microphone, the recording stops itself when the guest stops talking (or
 * `finish` stops it), the API writes down what was said, and `onHeard` gets the words to send as the guest's message.
 * A recording with nobody in it, a refused microphone or a failed transcription ends in a `notice` instead. `cancel`
 * throws the recording away, and any words still being written down for it. `sent` holds the last words sent, for a
 * screen reader to announce.
 */
export function useVoiceQuestion({ onHeard, language }: { onHeard: (text: string) => void; language: ChatLanguage }) {
   const [writing, setWriting] = useState(false);
   const [notice, setNotice] = useState<VoiceNotice | null>(null);
   const [sent, setSent] = useState<string | null>(null);
   const [asking, setAsking] = useState(false);
   // Each start and each cancel moves it on: words written down for an earlier one are dropped
   const question = useRef(0);
   const onHeardRef = useRef(onHeard);
   onHeardRef.current = onHeard;
   const languageRef = useRef(language);
   languageRef.current = language;

   const transcribe = useCallback(async (recording: Recording) => {
      const mine = question.current;
      if (recording.durationMs < VOICE_MIN_MS || recording.audio.size === 0) {
         setNotice('nothing');
         return;
      }
      setWriting(true);
      const result = await ChatService.transcribeSpeech(recording.audio, speakerLanguages(languageRef.current)).catch(
         () => ({ status: 'failed', reason: 'failed' }) as const
      );
      if (mine !== question.current) return;
      setWriting(false);
      if (result.status === 'heard') {
         setSent(result.text);
         onHeardRef.current(result.text);
      } else if (result.status === 'nothing') {
         setNotice('nothing');
      } else {
         setNotice(result.reason === 'too-many' ? 'tooMany' : result.reason === 'unavailable' ? 'unavailable' : 'failed');
      }
   }, []);

   const recorder = useVoiceRecorder({
      // Nobody spoke: nothing to send
      onAutoStop: (recording, reason) => (reason === 'nothing' ? setNotice('nothing') : void transcribe(recording))
   });
   const { start: startRecording, finish: finishRecording, cancel: cancelRecording } = recorder;

   const start = useCallback(async () => {
      question.current++;
      setNotice(null);
      setSent(null);
      setWriting(false);
      const state = await startRecording();
      const refused = refusalNotice[state];
      if (refused) setNotice(refused);
   }, [startRecording]);

   const finish = useCallback(async () => {
      const recording = await finishRecording();
      if (recording) await transcribe(recording);
   }, [finishRecording, transcribe]);

   const cancel = useCallback(() => {
      question.current++;
      cancelRecording();
      setWriting(false);
   }, [cancelRecording]);

   const dismiss = useCallback(() => setNotice(null), []);

   // Leaving drops whatever is still being written down
   useEffect(
      () => () => {
         question.current++;
      },
      []
   );

   // The first time, the browser asks the guest for the microphone: say so if the wait is more than a moment
   const waiting = recorder.state === 'starting';
   useEffect(() => {
      if (!waiting) {
         setAsking(false);
         return;
      }
      const timer = window.setTimeout(() => setAsking(true), VOICE_ASKING_MS);
      return () => window.clearTimeout(timer);
   }, [waiting]);

   const phase: VoicePhase = writing ? 'writing' : waiting ? 'starting' : recorder.state === 'recording' ? 'listening' : 'idle';

   return { phase, asking, notice, sent, elapsedMs: recorder.elapsedMs, readLevel: recorder.readLevel, start, finish, cancel, dismiss };
}
